import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'

/**
 * Brutalist dialog: sharp corners, thick border, spring snap-in (no fade
 * slide-up), Escape/backdrop-click to close. Renders via portal to escape
 * ancestor stacking contexts.
 */
export default function Modal({ open, onClose, title, header, ariaLabel, children, footer, maxWidth = 'max-w-2xl' }) {
    const dialogRef = useRef(null)

    useEffect(() => {
        if (!open) return
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        dialogRef.current?.focus()
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open, onClose])

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 pt-[8vh]"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) onClose()
                    }}
                >
                    <motion.div
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
                        tabIndex={-1}
                        initial={{ opacity: 0, scale: 0.96, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: 8 }}
                        transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.7 }}
                        className={`relative w-full ${maxWidth} border-2 border-black bg-white shadow-brutal-lg dark:border-white dark:bg-zinc-950`}
                    >
                        {header ? (
                            header
                        ) : (
                            title && (
                                <div className="flex items-center justify-between border-b-2 border-black px-4 py-3 dark:border-white">
                                    <h2 className="font-serif text-lg font-bold text-black dark:text-white">{title}</h2>
                                    <button
                                        onClick={onClose}
                                        aria-label="Close"
                                        className="px-2 py-1 font-mono text-lg leading-none text-black transition-colors hover:bg-black hover:text-white dark:text-white dark:hover:bg-white dark:hover:text-black"
                                    >
                                        ✕
                                    </button>
                                </div>
                            )
                        )}
                        <div className="max-h-[80vh] overflow-y-auto">{children}</div>
                        {footer && <div className="border-t-2 border-black px-4 py-3 dark:border-white">{footer}</div>}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    )
}
